import { forwardRef, memo, useCallback, useMemo } from "react";
import { useController } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { AutoHeight } from "../../AutoHeight/AutoHeight";
import { Box } from "../../ui/box";
import { Field } from "../../ui/field";
import { HStack } from "../../ui/h-stack";
import { Input } from "../../ui/input";
import { PasswordInput } from "../../ui/password-input";
import { Text } from "../../ui/text";
import { Textarea } from "../../ui/textarea";
import FormErrorMessage from "../FormErrorMessage";
import FormErrorObserver from "../FormErrorObserver";

/**
 * @typedef {object} Rules
 * @prop {boolean} [required]
 * @prop {number} [maxLength]
 * @prop {number} [minLength]
 * @prop {number} [max]
 * @prop {number} [min]
 * @prop {number} [step]
 * @prop {RegExp} [pattern]
 * @prop {(value: any) => boolean|Promise<boolean>|string} [validate]
 * @prop {boolean} [valueAsNumber]
 * @prop {boolean} [valueAsDate]
 * @prop {(value: any) => any} [setValueAs]
 * @prop {boolean} [disabled]
 * @prop {(e: import("react").SyntheticEvent) => void} [onChange]
 * @prop {(e: import("react").SyntheticEvent) => void} [onBlur]
 * @prop {any} [value]
 */

/**
 * @template TValue
 * @typedef {object} FieldProps
 * @prop {string} name
 * @prop {() => void} onBlur
 * @prop {(event: {target: { name: string, value: TValue}}) => void} onChange
 * @prop {TValue} value
 * @prop {boolean} required
 * @prop {boolean} disabled
 * @prop {boolean} invalid
 */

/**
 * @template {import("react-hook-form").FieldValues} TFieldValues
 * @template {import("react-hook-form").FieldPath<TFieldValues>} TName
 * @typedef {object} FormControllerRenderParams
 * @prop {import("react-hook-form").FieldPath<TFieldValues>} name
 * @prop {() => void} onBlur
 * @prop {(...event: any[]) => void} onChange
 * @prop {import("react-hook-form").FieldPathValue<TFieldValues, TName>} value
 * @prop {boolean} required
 * @prop {boolean} disabled
 * @prop {boolean} invalid
 * @prop {import("react-hook-form").RefCallBack} ref
 */

/**
 * A component that is used as a fundamental block of a form. Every field should be wrapped by this component.
 * @template {import("react-hook-form").FieldValues} TFieldValues
 * @template {import("react-hook-form").FieldPath<TFieldValues>} TName
 * @typedef {object} Props
 * @prop {import("react-hook-form").Control<TFieldValues>} control
 * @prop {TName} name
 * @prop {"text"|"text-numeric"|"textarea"|"password"|"date"} [type]
 * @prop {string} [label]
 * @prop {string} [placeholder]
 * @prop {import("react").ReactNode} [helperText]
 * @prop {boolean} [disabled]
 * @prop {any} [inputProps]
 * @prop {(params: FormControllerRenderParams<TFieldValues, TName>) => React.ReactNode} [render]
 * @prop {(params: FormControllerRenderParams<TFieldValues, TName>) => React.ReactNode} [renderWithFormControl]
 * @prop {Rules['required']} [required]
 * @prop {Rules['validate']} [validate]
 * @prop {Rules['onChange']} [onChange]
 * @prop {Rules['maxLength']} [maxLength] // used to display the max length of the input/textarea this is NOT a validation rule
 * @prop {Rules['minLength']} [minLength] // used to display the min length of the input/textarea this is NOT a validation rule
 * @prop {Rules} [deprecatedRules]
 * @prop {boolean} [withError] // if true, the component will render an error message if the field is invalid
 */

/**
 * @template {import("react-hook-form").FieldValues} TFieldValues
 * @template {import("react-hook-form").FieldPath<TFieldValues>} TName
 * @param {Props<TFieldValues, TName>} props
 * @param {import("react").ForwardedRef<any>} ref
 */
function _FormController(
  {
    control,
    name,
    deprecatedRules,
    type,
    label,
    placeholder,
    helperText,
    disabled = false,
    inputProps,
    render,
    renderWithFormControl,
    required = false,
    validate,
    onChange,
    maxLength,
    minLength,
    withError = true,
  },
  ref,
) {
  const rules = useMemo(() => {
    /** @type {Rules} */
    const rules = {
      required,
      validate,
      onChange,
      ...deprecatedRules,
    };
    return rules;
  }, [deprecatedRules, onChange, required, validate]);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });
  const { ref: fieldRef } = field;

  const mergedRefs = useCallback(
    /**
     * @param {any} node
     */
    (node) => {
      if (node) {
        fieldRef(node);
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }
    },
    [fieldRef, ref],
  );

  const invalid = Boolean(error);

  // const { isSubmitting } = useFormState();

  const commonFieldProps = {
    // disabled: isSubmitting ? true : disabled,
    disabled,
    required: rules.required ?? false,
  };

  // #region return
  return (
    <Box>
      {typeof render === "function" &&
        render({
          ...field,
          ...commonFieldProps,
          invalid,
          ref: mergedRefs,
        })}

      {render === undefined && (
        <Field label={label} {...commonFieldProps} invalid={invalid}>
          {typeof renderWithFormControl === "function" &&
            renderWithFormControl({
              ...field,
              ...commonFieldProps,
              invalid,
              ref: mergedRefs,
            })}

          {renderWithFormControl === undefined &&
            (() => {
              switch (type) {
                case "textarea":
                  return (
                    <Box w="full">
                      <Textarea
                        {...field}
                        {...commonFieldProps}
                        value={String(field?.value ?? "")}
                        placeholder={placeholder ?? label}
                        ref={mergedRefs}
                        autoresize
                        {...inputProps}
                      />
                    </Box>
                  );
                case "password":
                  return (
                    <PasswordInput
                      {...field}
                      {...commonFieldProps}
                      ref={mergedRefs}
                      type={type}
                      placeholder={placeholder ?? label}
                      value={String(field?.value ?? "")}
                      sx={{
                        "&:placeholder-shown": {
                          textTransform: "ellipsis",
                        },
                      }}
                      {...inputProps}
                    />
                  );
                default:
                  return (
                    <Box w="full">
                      <Input
                        {...field}
                        {...commonFieldProps}
                        ref={mergedRefs}
                        type={type === "text-numeric" ? "text" : type}
                        inputMode={
                          type === "text-numeric" ? "numeric" : undefined
                        }
                        placeholder={placeholder ?? label}
                        value={String(field?.value ?? "")}
                        sx={{
                          "&:placeholder-shown": {
                            textTransform: "ellipsis",
                          },
                        }}
                        {...inputProps}
                      />
                    </Box>
                  );
              }
            })()}
        </Field>
      )}

      <AutoHeight>
        <FormErrorObserver
          control={control}
          name={name}
          render={({ hasError, error }) =>
            helperText ||
            (maxLength !== undefined && minLength === undefined) ||
            hasError ? (
              <HStack
                w="full"
                alignItems="flex-start"
                justify="flex-end"
                mt="4px"
              >
                {(helperText || (error && withError)) && (
                  <Box flexGrow={1}>
                    <Text textStyle="text-sm" color="fg.muted">
                      {helperText}
                    </Text>

                    {hasError && <FormErrorMessage error={error} />}
                  </Box>
                )}

                {maxLength !== undefined && minLength === undefined && (
                  <Text
                    textStyle="text-sm"
                    color={
                      (field.value ?? "").length > maxLength
                        ? "red.500"
                        : "fg.muted"
                    }
                    flexShrink={0}
                  >
                    <FormattedMessage
                      defaultMessage="{count}/{max}"
                      values={{
                        count: String(field?.value ?? "").length,
                        max: maxLength,
                      }}
                    />
                  </Text>
                )}
              </HStack>
            ) : null
          }
        />
      </AutoHeight>
    </Box>
  );
}

// eslint-disable-next-line jsdoc/valid-types
/** @type {<TFieldValues extends import("react-hook-form").FieldValues, TName extends import("react-hook-form").FieldPath<TFieldValues>>(props: Props<TFieldValues, TName> & { ref?: import("react").MutableRefObject<any> }) => React.ReactElement} */
// @ts-ignore
export const FormController = memo(forwardRef(_FormController));
